import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { Button } from '../lib/button';
import { useDataSelection, HierarchicalSelection } from '../context/DataSelectionContext';
import {
  getMetadataSchema,
  getChildrenAtLevel,
  getFieldsForPath,
  MetadataSchema,
  HierarchyNode,
  Field,
} from '../api/service';
import { getPalette } from '../utils/paletteUtils';

interface SelectedField {
  fieldId: string; fieldLabel: string;
  tableId: string; tableLabel: string;
  domainId: string; domainLabel: string;
  rootIdx: number;
}

interface NavState {
  path: string[]; optionsAtLevel: HierarchyNode[][];
  depth: number; leafFields: Field[]; labels: string[];
}

type Toast = { message: string; type: 'success' | 'danger' } | null;

const ISS = {
  blue: '#0B419D',
  blueSoft: '#E8EEF8',
  blueWash: '#F3F6FB',
  ink: '#0F172A',
  muted: '#64748B',
  line: '#E8EDF3',
  white: '#FFFFFF',
};

const TB = {
  bar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0 32px', height: 64, flexShrink: 0,
    background: 'linear-gradient(90deg, #0B419D 0%, #1a5f8a 100%)',
    boxShadow: '0 2px 16px rgba(11,65,157,0.18)',
    position: 'sticky' as const, top: 0, zIndex: 100,
  },
  logoMark: {
    width: 36, height: 36, borderRadius: 8,
    background: 'rgba(255,255,255,0.15)',
    border: '1.5px solid rgba(255,255,255,0.25)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 17, fontWeight: 800, color: '#fff', letterSpacing: -1,
  } as React.CSSProperties,
  title: { fontSize: 15, fontWeight: 700, color: '#fff', opacity: 0.95, letterSpacing: 0.3 } as React.CSSProperties,
  userPill: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '6px 14px 6px 8px', borderRadius: 24,
    background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)',
  },
  avatar: {
    width: 30, height: 30, borderRadius: '50%',
    background: 'rgba(255,255,255,0.22)', border: '1.5px solid rgba(255,255,255,0.35)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 12, fontWeight: 700, color: '#fff', textTransform: 'uppercase' as const,
  },
  email: {
    fontSize: 13, color: 'rgba(255,255,255,0.9)', fontWeight: 500,
    maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const,
  },
  logoutBtn: {
    appearance: 'none' as const,
    background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.22)',
    borderRadius: 8, padding: '7px 14px', color: 'rgba(255,255,255,0.9)',
    fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', letterSpacing: 0.2,
  },
};

const STEPS = [
  { id: 0, label: 'Domain' },
  { id: 1, label: 'Table' },
  { id: 2, label: 'Fields' },
] as const;

const DOMAIN_HINTS: Record<string, string> = {
  sites: 'Locations & operational sites',
  site: 'Locations & operational sites',
  customers: 'Customer master records',
  customer: 'Customer master records',
  countries: 'Country reference data',
  country: 'Country reference data',
  cities: 'City & locality reference',
  city: 'City & locality reference',
};

function domainHint(label: string, id: string): string {
  const key = `${label} ${id}`.toLowerCase();
  for (const [needle, hint] of Object.entries(DOMAIN_HINTS)) {
    if (key.includes(needle)) return hint;
  }
  return 'Browse tables and fields';
}

function letterMark(label: string): string {
  const words = label.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return (label[0] ?? '?').toUpperCase();
}

const DataSelectionMenu: React.FC = () => {
  const navigate = useNavigate();
  const { saveSelection, selection: savedSelection } = useDataSelection();
  const savedSelectionKey = [
    savedSelection?.path?.join(',') ?? '',
    savedSelection?.selectedColumns?.join(',') ?? '',
  ].join('|');
  const { instance, accounts } = useMsal();

  const userEmail = accounts[0]?.username ?? accounts[0]?.name ?? '';
  const userInitials = userEmail ? userEmail.split('@')[0].slice(0, 2).toUpperCase() : '?';
  const handleLogout = () => instance.logoutRedirect();

  const [toast, setToast] = useState<Toast>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldQuery, setFieldQuery] = useState('');
  const [selected, setSelected] = useState<SelectedField[]>([]);
  const [nav, setNav] = useState<NavState>({
    path: [], optionsAtLevel: [], depth: 0, leafFields: [], labels: [],
  });
  const [rootIndexMap, setRootIndexMap] = useState<Map<string, number>>(new Map());

  const showToast = useCallback((message: string, type: 'success' | 'danger' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 2200);
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const schema: MetadataSchema = await getMetadataSchema();
        const idxMap = new Map<string, number>();
        schema.root.forEach((n, i) => idxMap.set(n.id, i));
        setRootIndexMap(idxMap);

        if (savedSelection?.path?.length) {
          const domainId = savedSelection.path[0];
          const tableId = savedSelection.path[savedSelection.path.length - 1];
          const domainNode = schema.root.find((node) => node.id === domainId);

          if (domainNode) {
            const domainLabel = domainNode.label;
            const rootIdx = idxMap.get(domainId) ?? 0;
            let children: HierarchyNode[] = [];
            let tableLabel = tableId;
            let leafFields: Field[] = [];

            try {
              children = await getChildrenAtLevel([domainId], 1);
            } catch {
              children = [];
            }

            const tableNode = children.find((node) => node.id === tableId);
            if (tableNode) {
              tableLabel = tableNode.label;
            }

            try {
              leafFields = await getFieldsForPath([domainId, tableId]);
            } catch {
              leafFields = [];
            }

            const fieldById = new Map(leafFields.map((field) => [field.id, field]));
            setSelected(savedSelection.selectedColumns.map((fieldId) => {
              const matchedField = fieldById.get(fieldId);
              return {
                fieldId,
                fieldLabel: matchedField?.label ?? fieldId,
                tableId,
                tableLabel,
                domainId,
                domainLabel,
                rootIdx,
              };
            }));

            if (leafFields.length > 0) {
              setNav({
                path: [domainId, tableId],
                optionsAtLevel: [schema.root, children],
                depth: 2,
                leafFields,
                labels: [domainLabel, tableLabel],
              });
            } else {
              setNav({
                path: [domainId],
                optionsAtLevel: [schema.root, children],
                depth: 1,
                leafFields: [],
                labels: [domainLabel],
              });
            }

            return;
          }
        }

        setSelected([]);
        setNav({ path: [], optionsAtLevel: [schema.root], depth: 0, leafFields: [], labels: [] });
      } catch (err) {
        setError(`Failed to load schema: ${err instanceof Error ? err.message : String(err)}`);
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [savedSelectionKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentRootIdx = nav.path[0] ? (rootIndexMap.get(nav.path[0]) ?? 0) : 0;

  const handleNodeClick = useCallback(async (node: HierarchyNode) => {
    if (busy) return;
    try {
      setBusy(true);
      setError(null);
      setFieldQuery('');
      const newPath = [...nav.path, node.id];
      const newLabels = [...nav.labels, node.label];
      const newDepth = nav.depth + 1;

      if (node.isLeaf || newDepth >= 2) {
        const fields = await getFieldsForPath(newPath);
        setNav((prev) => ({
          path: newPath,
          optionsAtLevel: [...prev.optionsAtLevel],
          depth: newDepth,
          leafFields: fields,
          labels: newLabels,
        }));
        return;
      }

      const children = await getChildrenAtLevel(newPath, newDepth);

      if (children.length === 0) {
        const domainId = newPath[0] ?? '';
        const domainLabel = newLabels[0] ?? '';
        const rootIdx = rootIndexMap.get(domainId) ?? 0;
        setSelected((prev) => {
          const exists = prev.find((s) => s.fieldId === node.id && s.tableId === node.id);
          if (exists) return prev.filter((s) => !(s.fieldId === node.id && s.tableId === node.id));
          return [...prev, {
            fieldId: node.id,
            fieldLabel: node.label,
            tableId: node.id,
            tableLabel: node.label,
            domainId,
            domainLabel,
            rootIdx,
          }];
        });
        return;
      }

      if (children.length === 1) {
        const onlyChild = children[0];
        const deepPath = [...newPath, onlyChild.id];
        const deepLabels = [...newLabels, onlyChild.label];
        const deepDepth = newDepth + 1;
        const fields = await getFieldsForPath(deepPath);
        setNav((prev) => ({
          path: deepPath,
          optionsAtLevel: [...prev.optionsAtLevel.slice(0, newDepth), children],
          depth: deepDepth,
          leafFields: fields,
          labels: deepLabels,
        }));
        return;
      }

      setNav((prev) => ({
        path: newPath,
        optionsAtLevel: [...prev.optionsAtLevel.slice(0, newDepth), children],
        depth: newDepth,
        leafFields: [],
        labels: newLabels,
      }));
    } catch (err) {
      setError(`Failed to load: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  }, [nav, busy, rootIndexMap]);

  const toggleField = useCallback((field: Field) => {
    const domainId = nav.path[0] ?? '';
    const domainLabel = nav.labels[0] ?? '';
    const tableId = nav.path[nav.path.length - 1] ?? '';
    const tableLabel = nav.labels[nav.labels.length - 1] ?? '';
    const rootIdx = rootIndexMap.get(domainId) ?? 0;
    setSelected((prev) => {
      const exists = prev.find((s) => s.fieldId === field.id && s.tableId === tableId);
      if (exists) return prev.filter((s) => !(s.fieldId === field.id && s.tableId === tableId));
      return [...prev, {
        fieldId: field.id,
        fieldLabel: field.label,
        tableId,
        tableLabel,
        domainId,
        domainLabel,
        rootIdx,
      }];
    });
  }, [nav, rootIndexMap]);

  const handleBack = useCallback(() => {
    if (nav.depth === 0) return;
    setFieldQuery('');
    const newDepth = nav.depth - 1;
    const newPath = nav.path.slice(0, newDepth);
    const newLabels = nav.labels.slice(0, newDepth);
    setNav((prev) => ({
      path: newPath,
      optionsAtLevel: prev.optionsAtLevel.slice(0, newDepth + 1),
      depth: newDepth,
      leafFields: [],
      labels: newLabels,
    }));
  }, [nav]);

  const removeField = useCallback(
    (f: SelectedField) => setSelected((prev) => prev.filter((s) => !(s.fieldId === f.fieldId && s.tableId === f.tableId))),
    [],
  );
  const handleClear = useCallback(() => {
    setSelected([]);
    showToast('Selection cleared', 'danger');
  }, [showToast]);

  const handleSave = useCallback(() => {
    if (!selected.length) return showToast('Nothing selected', 'danger');
    try {
      const [first] = selected;
      const hasMixedTables = selected.some(
        (item) => item.domainId !== first.domainId || item.tableId !== first.tableId,
      );
      if (hasMixedTables) {
        showToast('Select fields from a single table before saving', 'danger');
        return;
      }

      const sel: HierarchicalSelection = {
        path: [first.domainId, first.tableId],
        selectedColumns: selected.map((s) => s.fieldId),
      };
      saveSelection(sel);
      showToast(`Saved ${selected.length} field${selected.length > 1 ? 's' : ''}`, 'success');
      setTimeout(() => navigate('/master-data', { state: { transition: 'data-to-master' } }), 500);
    } catch (err) {
      setError(`Failed to save: ${err instanceof Error ? err.message : String(err)}`);
    }
  }, [selected, saveSelection, navigate, showToast]);

  const isLeafMode = nav.depth >= 2;
  const activeStep = isLeafMode ? 2 : nav.depth;
  const browseNodes = nav.optionsAtLevel[nav.depth] ?? [];
  const leafPal = getPalette(currentRootIdx, 1);
  const tableId = nav.path[nav.path.length - 1] ?? '';

  const filteredFields = useMemo(() => {
    const normalizedQuery = fieldQuery.trim().toLowerCase();
    if (!normalizedQuery) return nav.leafFields;
    return nav.leafFields.filter(
      (field) =>
        field.label.toLowerCase().includes(normalizedQuery) ||
        field.id.toLowerCase().includes(normalizedQuery),
    );
  }, [nav.leafFields, fieldQuery]);

  const selectedCountInTable = useMemo(
    () => selected.filter((s) => s.tableId === tableId).length,
    [selected, tableId],
  );

  const selectAllVisible = useCallback(() => {
    const domainId = nav.path[0] ?? '';
    const domainLabel = nav.labels[0] ?? '';
    const currentTableId = nav.path[nav.path.length - 1] ?? '';
    const tableLabel = nav.labels[nav.labels.length - 1] ?? '';
    const rootIdx = rootIndexMap.get(domainId) ?? 0;
    setSelected((prev) => {
      const next = [...prev];
      for (const field of filteredFields) {
        const exists = next.some((s) => s.fieldId === field.id && s.tableId === currentTableId);
        if (!exists) {
          next.push({
            fieldId: field.id,
            fieldLabel: field.label,
            tableId: currentTableId,
            tableLabel,
            domainId,
            domainLabel,
            rootIdx,
          });
        }
      }
      return next;
    });
  }, [filteredFields, nav, rootIndexMap]);

  const clearVisible = useCallback(() => {
    const visibleIds = new Set(filteredFields.map((f) => f.id));
    setSelected((prev) => prev.filter((s) => !(s.tableId === tableId && visibleIds.has(s.fieldId))));
  }, [filteredFields, tableId]);

  const grouped = useMemo(() => {
    const map = new Map<string, {
      domainLabel: string;
      rootIdx: number;
      tables: Map<string, { tableLabel: string; fields: SelectedField[] }>;
    }>();
    for (const f of selected) {
      if (!map.has(f.domainId)) {
        map.set(f.domainId, { domainLabel: f.domainLabel, rootIdx: f.rootIdx, tables: new Map() });
      }
      const domain = map.get(f.domainId)!;
      if (!domain.tables.has(f.tableId)) {
        domain.tables.set(f.tableId, { tableLabel: f.tableLabel, fields: [] });
      }
      domain.tables.get(f.tableId)!.fields.push(f);
    }
    return [...map.values()];
  }, [selected]);

  const stepTitle = isLeafMode
    ? 'Choose fields'
    : nav.depth === 0
      ? 'Choose a domain'
      : 'Choose a table';

  const stepHint = isLeafMode
    ? 'Search and tick the columns you need for this table.'
    : nav.depth === 0
      ? 'Start with the data area you want to work in.'
      : 'Pick the table that holds the fields you need.';

  const pageBg = {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column' as const,
    background: `
      radial-gradient(1200px 480px at 12% -10%, rgba(11,65,157,0.14), transparent 55%),
      radial-gradient(900px 420px at 100% 0%, rgba(26,95,138,0.10), transparent 50%),
      linear-gradient(180deg, #F5F7FB 0%, #EEF2F7 100%)
    `,
    fontFamily: "'DM Sans','Segoe UI',sans-serif",
  };

  if (loading) {
    return (
      <div style={pageBg}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontSize: 14, color: ISS.muted }}>Loading data catalogue…</span>
        </div>
      </div>
    );
  }

  return (
    <div style={pageBg}>
      <style>{`
        @keyframes slideIn { from { opacity:0; transform:translateX(28px) } to { opacity:1; transform:translateX(0) } }
        @keyframes popIn   { from { opacity:0; transform:scale(0.92) }     to { opacity:1; transform:scale(1) } }
        @keyframes fadeUp  { from { opacity:0; transform:translateY(10px) } to { opacity:1; transform:translateY(0) } }
        .ds-logout:hover { background: rgba(255,255,255,0.2) !important; border-color: rgba(255,255,255,0.4) !important; color: #fff !important; }
        .ds-domain { transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease; }
        .ds-domain:hover { transform: translateY(-2px); box-shadow: 0 10px 24px rgba(15,23,42,0.08); border-color: #cbd5e1 !important; }
        .ds-table-row { transition: background 0.12s ease, border-color 0.12s ease; }
        .ds-table-row:hover { background: ${ISS.blueWash} !important; border-color: #c7d2e5 !important; }
        .ds-field-row { transition: background 0.12s ease; }
        .ds-field-row:hover { background: #F8FAFC !important; }
        .ds-field-row:hover .ds-field-id { opacity: 1 !important; }
        .ds-search:focus { outline: none; border-color: ${ISS.blue} !important; box-shadow: 0 0 0 3px rgba(11,65,157,0.12); }
        .ds-ghost:hover:not(:disabled) { background: ${ISS.blueWash} !important; border-color: #c7d2e5 !important; }
        .ds-ghost:disabled { opacity: 0.45; cursor: not-allowed; }
      `}</style>

      <div style={TB.bar}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={TB.logoMark}>M</div>
          <span style={TB.title}>Data Selection</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {userEmail && (
            <div style={TB.userPill}>
              <div style={TB.avatar}>{userInitials}</div>
              <span style={TB.email}>{userEmail}</span>
            </div>
          )}
          <button type="button" className="ds-logout" onClick={handleLogout} style={TB.logoutBtn}>
            Sign out
          </button>
        </div>
      </div>

      <div style={{ flex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '28px 24px 40px' }}>
        <div style={{
          display: 'flex', gap: 22, alignItems: 'stretch',
          maxWidth: 1140, width: '100%', animation: 'fadeUp 0.4s ease-out',
        }}>

          {/* Browse panel */}
          <div style={{
            flex: 1.5, minWidth: 0, background: ISS.white, borderRadius: 18,
            border: `1px solid ${ISS.line}`,
            boxShadow: '0 8px 30px rgba(15,23,42,0.06)',
            display: 'flex', flexDirection: 'column', minHeight: 540, maxHeight: 'calc(100vh - 140px)',
            overflow: 'hidden',
          }}>
            {/* Accent hairline */}
            <div style={{ height: 3, background: `linear-gradient(90deg, ${ISS.blue}, #1a5f8a 55%, transparent)` }} />

            <div style={{ padding: '18px 22px 16px', borderBottom: `1px solid ${ISS.line}` }}>
              {/* Step rail */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 16 }}>
                {STEPS.map((step, i) => {
                  const done = activeStep > step.id;
                  const current = activeStep === step.id;
                  return (
                    <React.Fragment key={step.id}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{
                          width: 24, height: 24, borderRadius: '50%',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 11, fontWeight: 700,
                          background: done || current ? ISS.blue : '#E2E8F0',
                          color: done || current ? '#fff' : '#94A3B8',
                          boxShadow: current ? '0 0 0 4px rgba(11,65,157,0.14)' : 'none',
                          transition: 'background 0.25s ease, box-shadow 0.25s ease',
                        }}>
                          {done ? '✓' : step.id + 1}
                        </div>
                        <span style={{
                          fontSize: 12, fontWeight: current ? 700 : 600,
                          color: current ? ISS.blue : done ? '#334155' : '#94A3B8',
                          letterSpacing: '0.02em',
                        }}>
                          {step.label}
                        </span>
                      </div>
                      {i < STEPS.length - 1 && (
                        <div style={{
                          width: 28, height: 2, margin: '0 10px', borderRadius: 2,
                          background: activeStep > step.id ? ISS.blue : '#E2E8F0',
                          transition: 'background 0.25s ease',
                        }} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{
                    fontSize: 11, fontWeight: 650, letterSpacing: '0.06em',
                    color: ISS.muted, marginBottom: 6,
                  }}>
                    {['Home', ...nav.labels].join(' › ')}
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 750, color: ISS.ink, letterSpacing: '-0.03em' }}>
                    {stepTitle}
                  </div>
                  <div style={{ fontSize: 13, color: ISS.muted, marginTop: 5, lineHeight: 1.4 }}>
                    {stepHint}
                  </div>
                </div>
                {nav.depth > 0 && (
                  <button
                    type="button"
                    className="ds-ghost"
                    onClick={handleBack}
                    disabled={busy}
                    style={{
                      flexShrink: 0, border: `1px solid ${ISS.line}`, background: ISS.white,
                      borderRadius: 10, padding: '8px 12px', fontSize: 13, fontWeight: 600,
                      color: '#334155', cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit',
                    }}
                  >
                    ← Back
                  </button>
                )}
              </div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '18px 18px 22px', animation: 'fadeUp 0.28s ease-out' }} key={`step-${activeStep}-${nav.path.join('.')}`}>
              {!isLeafMode && nav.depth === 0 && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: 12,
                }}>
                  {browseNodes.length === 0 && !error && (
                    <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>No domains available.</p>
                  )}
                  {browseNodes.map((node) => {
                    const rootIdx = rootIndexMap.get(node.id) ?? 0;
                    const pal = getPalette(rootIdx, 0);
                    return (
                      <button
                        key={node.id}
                        type="button"
                        className="ds-domain"
                        disabled={busy}
                        onClick={() => void handleNodeClick(node)}
                        style={{
                          textAlign: 'left',
                          border: `1px solid ${ISS.line}`,
                          borderLeft: `4px solid ${pal.ring}`,
                          background: ISS.white,
                          color: ISS.ink,
                          borderRadius: 14,
                          padding: '16px 14px',
                          cursor: busy ? 'wait' : 'pointer',
                          fontFamily: 'inherit',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 12,
                          minHeight: 96,
                        }}
                      >
                        <div style={{
                          width: 40, height: 40, borderRadius: 11, flexShrink: 0,
                          background: pal.bg, color: pal.text,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 14, fontWeight: 800, letterSpacing: '0.02em',
                        }}>
                          {letterMark(node.label)}
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: 750, lineHeight: 1.25, marginBottom: 4 }}>
                            {node.label}
                          </div>
                          <div style={{ fontSize: 12, color: ISS.muted, lineHeight: 1.35 }}>
                            {domainHint(node.label, node.id)}
                          </div>
                        </div>
                        <span style={{ color: '#94A3B8', fontSize: 18, lineHeight: 1, marginTop: 2 }}>›</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {!isLeafMode && nav.depth === 1 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {browseNodes.length === 0 && !error && (
                    <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>No tables in this domain.</p>
                  )}
                  {browseNodes.map((node) => {
                    const pal = getPalette(currentRootIdx, 0);
                    return (
                      <button
                        key={node.id}
                        type="button"
                        className="ds-table-row"
                        disabled={busy}
                        onClick={() => void handleNodeClick(node)}
                        style={{
                          textAlign: 'left',
                          border: `1px solid ${ISS.line}`,
                          background: ISS.white,
                          color: ISS.ink,
                          borderRadius: 12,
                          padding: '14px 16px',
                          cursor: busy ? 'wait' : 'pointer',
                          fontFamily: 'inherit',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                        }}
                      >
                        <div style={{
                          width: 8, height: 8, borderRadius: '50%', background: pal.ring, flexShrink: 0,
                        }} />
                        <span style={{ flex: 1, fontSize: 14, fontWeight: 650 }}>{node.label}</span>
                        <span style={{ color: '#94A3B8', fontSize: 18 }}>›</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {isLeafMode && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
                      <span style={{
                        position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                        color: '#94A3B8', fontSize: 14, pointerEvents: 'none',
                      }}>
                        ⌕
                      </span>
                      <input
                        className="ds-search"
                        type="search"
                        value={fieldQuery}
                        onChange={(e) => setFieldQuery(e.target.value)}
                        placeholder="Search fields…"
                        style={{
                          width: '100%', boxSizing: 'border-box',
                          border: `1.5px solid ${ISS.line}`, borderRadius: 11,
                          padding: '11px 12px 11px 34px', fontSize: 13, fontFamily: 'inherit',
                          color: ISS.ink, background: ISS.white,
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      className="ds-ghost"
                      onClick={selectAllVisible}
                      disabled={busy || filteredFields.length === 0}
                      style={{
                        border: `1px solid ${ISS.line}`, background: ISS.white, borderRadius: 10,
                        padding: '9px 12px', fontSize: 12, fontWeight: 600, color: '#334155',
                        cursor: 'pointer', fontFamily: 'inherit',
                      }}
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      className="ds-ghost"
                      onClick={clearVisible}
                      disabled={busy || selectedCountInTable === 0}
                      style={{
                        border: `1px solid ${ISS.line}`, background: ISS.white, borderRadius: 10,
                        padding: '9px 12px', fontSize: 12, fontWeight: 600, color: '#334155',
                        cursor: 'pointer', fontFamily: 'inherit',
                      }}
                    >
                      Clear visible
                    </button>
                  </div>

                  <div style={{ fontSize: 12, color: ISS.muted }}>
                    <span style={{ fontWeight: 700, color: ISS.blue }}>{selectedCountInTable}</span> selected
                    {fieldQuery.trim()
                      ? ` · showing ${filteredFields.length} of ${nav.leafFields.length}`
                      : ` · ${nav.leafFields.length} fields`}
                  </div>

                  <div style={{
                    border: `1px solid ${ISS.line}`, borderRadius: 14, overflow: 'hidden',
                    background: ISS.white,
                  }}>
                    {filteredFields.length === 0 ? (
                      <p style={{ margin: 0, padding: 18, fontSize: 13, color: '#94a3b8' }}>
                        No fields match “{fieldQuery.trim()}”.
                      </p>
                    ) : (
                      filteredFields.map((field, index) => {
                        const checked = selected.some((s) => s.fieldId === field.id && s.tableId === tableId);
                        return (
                          <label
                            key={field.id}
                            className="ds-field-row"
                            style={{
                              display: 'flex', alignItems: 'center', gap: 12,
                              padding: '12px 14px 12px 0', cursor: busy ? 'wait' : 'pointer',
                              borderTop: index === 0 ? 'none' : `1px solid ${ISS.line}`,
                              background: checked ? ISS.blueWash : ISS.white,
                              borderLeft: checked ? `3px solid ${ISS.blue}` : '3px solid transparent',
                              paddingLeft: 11,
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={busy}
                              onChange={() => toggleField(field)}
                              style={{ width: 16, height: 16, accentColor: ISS.blue, cursor: 'pointer' }}
                            />
                            <span style={{
                              flex: 1, fontSize: 13.5, fontWeight: checked ? 700 : 550,
                              color: checked ? ISS.blue : ISS.ink,
                            }}>
                              {field.label}
                            </span>
                            <span
                              className="ds-field-id"
                              style={{
                                fontSize: 11, color: '#94a3b8', fontFamily: 'ui-monospace, monospace',
                                opacity: 0, transition: 'opacity 0.15s ease',
                              }}
                            >
                              {field.id}
                            </span>
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Selected fields tray */}
          <div style={{
            flex: 0.88, minWidth: 260, maxWidth: 360, background: ISS.white, borderRadius: 18,
            border: `1px solid ${ISS.line}`,
            display: 'flex', flexDirection: 'column',
            maxHeight: 'calc(100vh - 140px)', overflow: 'hidden',
            boxShadow: '0 8px 30px rgba(15,23,42,0.06)',
          }}>
            <div style={{ height: 3, background: `linear-gradient(90deg, ${ISS.blue}, transparent)` }} />
            <div style={{ padding: '18px 18px 0', display: 'flex', flexDirection: 'column', gap: 14, flex: 1, overflowY: 'auto' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: ISS.ink, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                  Selected fields
                </div>
                <div style={{
                  fontSize: 12, fontWeight: 700, color: ISS.blue, background: ISS.blueSoft,
                  borderRadius: 999, padding: '3px 9px', minWidth: 28, textAlign: 'center',
                }}>
                  {selected.length}
                </div>
              </div>
              <hr style={{ border: 'none', borderTop: `1px solid ${ISS.line}`, margin: 0 }} />

              {error && (
                <div style={{
                  fontSize: 12, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca',
                  borderRadius: 10, padding: '10px 12px',
                }}>
                  ⚠ {error}
                </div>
              )}

              {selected.length === 0 ? (
                <div style={{
                  flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', textAlign: 'center', padding: '28px 12px', gap: 10,
                }}>
                  <div style={{
                    width: 52, height: 52, borderRadius: 16, background: ISS.blueWash,
                    border: `1px dashed #c7d2e5`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: ISS.blue, fontSize: 20, fontWeight: 700,
                  }}>
                    +
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#334155' }}>Nothing selected yet</div>
                  <p style={{ fontSize: 13, color: ISS.muted, margin: 0, lineHeight: 1.45, maxWidth: 220 }}>
                    Choose a domain and table, then tick the fields you need.
                  </p>
                </div>
              ) : (
                grouped.map(({ domainLabel, rootIdx, tables }) => {
                  const domPal = getPalette(rootIdx, 0);
                  return (
                    <div key={domainLabel}>
                      <div style={{
                        fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em',
                        color: domPal.text, marginBottom: 8, paddingBottom: 4,
                        borderBottom: `1px solid ${domPal.ring}33`,
                      }}>
                        {domainLabel}
                      </div>
                      {[...tables.values()].map(({ tableLabel, fields }) => (
                        <div key={tableLabel} style={{ marginBottom: 10 }}>
                          <div style={{
                            fontSize: 10, fontWeight: 600, color: '#9ca3af',
                            letterSpacing: '0.05em', marginBottom: 6, paddingLeft: 2,
                          }}>
                            {tableLabel}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                            {fields.map((f) => (
                              <span
                                key={f.fieldId}
                                style={{
                                  display: 'inline-flex', alignItems: 'center', justifyContent: 'space-between',
                                  padding: '7px 10px 7px 12px', borderRadius: 10,
                                  border: `1px solid ${ISS.line}`, background: ISS.blueWash,
                                  color: ISS.blue, fontSize: 12, fontWeight: 650,
                                  animation: 'popIn 0.2s ease-out',
                                }}
                              >
                                {f.fieldLabel}
                                <button
                                  type="button"
                                  onClick={() => removeField(f)}
                                  onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.45'; }}
                                  style={{
                                    background: 'none', border: 'none', fontSize: 15, lineHeight: 1,
                                    paddingLeft: 8, cursor: 'pointer', opacity: 0.45,
                                    color: ISS.blue, fontFamily: 'inherit', transition: 'opacity 0.15s',
                                  }}
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })
              )}
            </div>

            <div style={{
              display: 'flex', gap: 10, padding: '14px 18px 18px',
              borderTop: `1px solid ${ISS.line}`, background: '#FAFBFC',
            }}>
              <Button onClick={handleClear} size="small" disabled={selected.length === 0}>Clear All</Button>
              <Button onClick={handleSave} variant="primary" size="small" disabled={selected.length === 0}>
                Save &amp; Continue
              </Button>
            </div>
          </div>
        </div>
      </div>

      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, padding: '12px 22px', borderRadius: 10,
          color: '#fff', fontSize: 13, fontWeight: 600,
          background: toast.type === 'success' ? '#16a34a' : '#dc2626',
          boxShadow: '0 8px 24px rgba(0,0,0,0.2)', zIndex: 1000,
          animation: 'slideIn 0.35s cubic-bezier(0.34,1.56,0.64,1)',
        }}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default DataSelectionMenu;
